import { useEffect, useState } from 'react';
import { Box, Divider, Flex, Heading, VStack } from '@chakra-ui/react';
import * as internalToolsApi from 'api/internaltools';
import { snakeToTitleCase } from 'utils/utils';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';
import ShowKeyAndValue from 'components/ShowKeyAndValue/ShowKeyAndValue';

const ADDRESS_FIELDS = ['street_line_1', 'street_line_2', 'city', 'state', 'postal_code', 'country'];

const formatParentLabel = (parent) => {
  if (!parent) return 'Other Details';
  if (parent === 'business') return 'Business Details';

  const match = parent.match(/^([a-z]+)_(\d+)$/i);

  if (match) {
    return `${snakeToTitleCase(match[1])} ${Number(match[2]) + 1}`;
  }

  return snakeToTitleCase(parent);
};

// `business` first, then controllers, then intermediaries, then anything else.
const getParentSortKey = (parent) => {
  if (parent === 'business') return [0, 0];

  const match = parent?.match(/^([a-z]+)_(\d+)$/i);

  if (match) {
    const groupOrder = match[1] === 'controller' ? 1 : match[1] === 'intermediary' ? 2 : 3;

    return [groupOrder, Number(match[2])];
  }

  return [4, 0];
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Not available';

  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      const addressParts = ADDRESS_FIELDS.map((field) => parsed[field]).filter(Boolean);

      if (addressParts.length > 0) return addressParts.join(', ');

      return Object.entries(parsed)
        .filter(([, val]) => val !== '' && val !== null && val !== undefined)
        .map(([key, val]) => `${snakeToTitleCase(key)}: ${val}`)
        .join(', ');
    } catch {
      return value;
    }
  }

  return value;
};

export default function KybComplianceDetails({ kybId, onStatusLoaded }) {
  const [groupedDetails, setGroupedDetails] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchKybDetails = async () => {
    if (!kybId) return;

    setIsLoading(true);
    try {
      const response = await internalToolsApi.getExhaustiveKybDetailsById(
        kybId,
        process.env.REACT_APP_KYB_VIEW_PASSWORD
      );
      const data = response?.data?.data;
      const details = data?.details || [];

      onStatusLoaded?.({ status: data?.status, lastUpdatedAt: data?.last_updated_at });

      const groups = details.reduce((acc, detail) => {
        const parent = detail?.parent || 'other';

        if (!acc[parent]) acc[parent] = [];
        acc[parent].push(detail);

        return acc;
      }, {});

      const orderedGroups = Object.entries(groups)
        .map(([parent, fields]) => ({
          parent,
          label: formatParentLabel(parent),
          fields: [...fields].sort((a, b) => (a?.key || '').localeCompare(b?.key || '')),
        }))
        .sort((a, b) => {
          const [aGroup, aIndex] = getParentSortKey(a.parent);
          const [bGroup, bIndex] = getParentSortKey(b.parent);

          return aGroup - bGroup || aIndex - bIndex;
        });

      setGroupedDetails(orderedGroups);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchKybDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kybId]);

  return (
    <Box>
      <LoadingErrorWrapper isLoading={isLoading} error={error} errorTitle='Error fetching KYB details.'>
        <VStack align='stretch' gap={4}>
          {groupedDetails.map((group, groupIdx) => (
            <Box key={group.parent}>
              {groupIdx > 0 && <Divider variant='dashed' mb={4} />}
              <Heading as='h1' size='lg' mb={3}>
                {group.label}
              </Heading>
              <Flex direction='column' gap={3}>
                {group.fields.map((field) => (
                  <ShowKeyAndValue
                    key={field?.id}
                    keyName={snakeToTitleCase(field?.key || '')}
                    value={formatValue(field?.value)}
                  />
                ))}
              </Flex>
            </Box>
          ))}
        </VStack>
      </LoadingErrorWrapper>
    </Box>
  );
}
